import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useChatContextNotes } from "@/contexts/ChatContextNotesContext";
import { api, extractErrorCode } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  BookMarked,
  Search,
  Pin,
  PinOff,
  Edit2,
  Trash2,
  Plus,
  Loader2,
  Scale,
  MessageSquare,
  FileText,
  PencilLine,
  Filter,
  X,
  ChevronRight,
  Lock,
  Send,
} from "lucide-react";
import { toast } from "sonner";

const SOURCE_ICON = {
  jurisprudence: Scale,
  chat: MessageSquare,
  document: FileText,
  manual: PencilLine,
};

const KB_ALLOWED_TIERS = ["legal_pro", "business"];

export default function LegalKnowledge() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { notes: ctxNotes, addNote: addCtxNote, max: maxCtx } = useChatContextNotes();
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ per_source: {}, top_tags: [] });
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [activeSource, setActiveSource] = useState("all");
  const [activeTag, setActiveTag] = useState(null);

  // Create/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState({
    title: "",
    body: "",
    citation: "",
    tags: "",
    source: "manual",
  });

  const isAllowed = user && KB_ALLOWED_TIERS.includes(user.subscription_tier);

  const loadAll = async () => {
    if (!isAllowed) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQ.trim()) params.set("q", searchQ.trim());
      if (activeSource !== "all") params.set("source", activeSource);
      if (activeTag) params.set("tag", activeTag);
      const [listResp, statsResp] = await Promise.all([
        api.get(`/legal/knowledge?${params.toString()}`),
        api.get("/legal/knowledge/stats"),
      ]);
      setNotes(listResp.data.notes || []);
      setTotal(listResp.data.total || 0);
      setStats(statsResp.data || { per_source: {}, top_tags: [] });
    } catch (e) {
      toast.error(t("legal_kb.err_load"));
    } finally {
      setLoading(false);
    }
  };

  useImperativeLoad(() => {
    loadAll();
  }, [user?.user_id, activeSource, activeTag]);

  const submitSearch = (e) => {
    e?.preventDefault();
    loadAll();
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", body: "", citation: "", tags: "", source: "manual" });
    setDialogOpen(true);
  };

  const openEdit = (note) => {
    setEditing(note);
    setForm({
      title: note.title || "",
      body: note.body || "",
      citation: note.citation || "",
      tags: (note.tags || []).join(", "),
      source: note.source || "manual",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error(t("legal_kb.err_required"));
      return;
    }
    const tagList = form.tags
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      if (editing) {
        const r = await api.patch(`/legal/knowledge/${editing.note_id}`, {
          title: form.title.trim(),
          body: form.body.trim(),
          citation: form.citation.trim() || null,
          tags: tagList,
        });
        setNotes((cur) =>
          cur.map((n) => (n.note_id === editing.note_id ? r.data.note : n))
        );
        toast.success(t("legal_kb.toast_updated"));
      } else {
        await api.post("/legal/knowledge", {
          title: form.title.trim(),
          body: form.body.trim(),
          citation: form.citation.trim() || null,
          tags: tagList,
          source: "manual",
        });
        toast.success(t("legal_kb.toast_created"));
        loadAll();
      }
      setDialogOpen(false);
    } catch (err) {
      const code = extractErrorCode(err);
      toast.error(t(`legal_kb.err_${code}`, { _default: t("legal_kb.err_save") }));
    }
  };

  const handleTogglePin = async (note) => {
    try {
      const r = await api.patch(`/legal/knowledge/${note.note_id}`, {
        pinned: !note.pinned,
      });
      // Re-load so pinned ordering is correct
      setNotes((cur) =>
        [...cur.map((n) => (n.note_id === note.note_id ? r.data.note : n))].sort(
          (a, b) => {
            if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
            return (b.updated_at || "").localeCompare(a.updated_at || "");
          }
        )
      );
    } catch (e) {
      toast.error(t("legal_kb.err_save"));
    }
  };

  const handleDelete = async (note) => {
    if (!window.confirm(t("legal_kb.confirm_delete"))) return;
    try {
      await api.delete(`/legal/knowledge/${note.note_id}`);
      setNotes((cur) => cur.filter((n) => n.note_id !== note.note_id));
      setTotal((t) => Math.max(0, t - 1));
      toast.success(t("legal_kb.toast_deleted"));
    } catch (e) {
      toast.error(t("legal_kb.err_delete"));
    }
  };

  const handleUseInChat = (note, { goToChat = true } = {}) => {
    const isAlready = ctxNotes.some((n) => n.note_id === note.note_id);
    if (isAlready) {
      toast.info(t("legal_kb.use_in_chat_already"));
      if (goToChat) navigate("/chat");
      return;
    }
    const r = addCtxNote(note);
    if (r.added) {
      toast.success(t("legal_kb.use_in_chat_added"));
      if (goToChat) navigate("/chat");
    } else if (r.reason === "max") {
      toast.error(t("legal_kb.use_in_chat_max", { max: maxCtx }));
    }
  };

  const sourceTabs = useMemo(
    () => [
      { key: "all", label: t("legal_kb.source_all"), count: total },
      {
        key: "jurisprudence",
        label: t("legal_kb.source_jurisprudence"),
        count: stats.per_source.jurisprudence || 0,
      },
      {
        key: "chat",
        label: t("legal_kb.source_chat"),
        count: stats.per_source.chat || 0,
      },
      {
        key: "document",
        label: t("legal_kb.source_document"),
        count: stats.per_source.document || 0,
      },
      {
        key: "manual",
        label: t("legal_kb.source_manual"),
        count: stats.per_source.manual || 0,
      },
    ],
    [stats, t, total]
  );

  // Upsell page for non-allowed tiers
  if (!isAllowed) {
    return (
      <div className="h-full overflow-y-auto bg-[#0A0C1F]" data-testid="legal-kb-upsell">
        <section className="mx-auto max-w-3xl px-6 py-16 lg:px-10">
          <div className="rounded-3xl border border-[#D4A937]/30 bg-gradient-to-br from-[#0B0E5C] to-[#05073B] p-10 text-center shadow-xl">
            <Lock className="mx-auto h-10 w-10 text-[#D4A937]" />
            <h1 className="mt-4 font-display text-3xl font-extrabold text-white">
              {t("legal_kb.upsell_title")}
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
              {t("legal_kb.upsell_body")}
            </p>
            <ul className="mx-auto mt-6 max-w-md space-y-2 text-left text-sm text-slate-200">
              <li className="flex items-start gap-2">
                <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A937]" />
                {t("legal_kb.upsell_bullet1")}
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A937]" />
                {t("legal_kb.upsell_bullet2")}
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A937]" />
                {t("legal_kb.upsell_bullet3")}
              </li>
            </ul>
            <Link
              to="/pricing"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4A937] px-6 py-3 text-sm font-bold text-[#05073B] hover:bg-[#E0B850]"
              data-testid="legal-kb-upsell-cta"
            >
              <Scale className="h-4 w-4" />
              {t("legal_kb.upsell_cta")}
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-[#0A0C1F]" data-testid="legal-kb-page">
      <section className="mx-auto max-w-6xl px-6 py-10 lg:px-10">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#D4A937]/30 bg-[#D4A937]/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#F4C955]">
              <BookMarked className="h-3 w-3" />
              {t("legal_kb.pretitle")}
            </span>
            <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              {t("legal_kb.title")}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              {t("legal_kb.subtitle", { count: total })}
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="self-start rounded-full bg-[#D4A937] font-bold text-[#05073B] hover:bg-[#E0B850]"
            data-testid="legal-kb-new-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("legal_kb.new_note")}
          </Button>
        </div>

        {/* Search bar */}
        <form
          onSubmit={submitSearch}
          className="mb-5 flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-900 p-2"
          data-testid="legal-kb-search-form"
        >
          <Search className="ml-3 h-4 w-4 shrink-0 text-slate-500" />
          <Input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder={t("legal_kb.search_placeholder")}
            className="flex-1 border-0 bg-transparent text-sm text-white focus-visible:ring-0"
            data-testid="legal-kb-search-input"
          />
          {searchQ && (
            <button
              type="button"
              onClick={() => {
                setSearchQ("");
                loadAll();
              }}
              className="rounded-full p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
              aria-label="clear"
              data-testid="legal-kb-search-clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <Button
            type="submit"
            disabled={loading}
            className="h-9 rounded-full bg-[#9ACD32] px-4 text-xs font-bold text-[#05073B] hover:bg-[#A8DF38]"
            data-testid="legal-kb-search-submit"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("legal_kb.search_btn")}
          </Button>
        </form>

        {/* Source tabs */}
        <div className="mb-5 flex flex-wrap gap-2" data-testid="legal-kb-source-tabs">
          {sourceTabs.map((tab) => {
            const active = activeSource === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveSource(tab.key)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "border-[#D4A937] bg-[#D4A937]/15 text-[#F4C955]"
                    : "border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-600"
                }`}
                data-testid={`legal-kb-source-tab-${tab.key}`}
              >
                <span>{tab.label}</span>
                <span
                  className={`rounded-full px-1.5 text-[10px] ${
                    active ? "bg-[#D4A937]/20 text-[#F4C955]" : "bg-slate-800 text-slate-500"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Top tags */}
        {stats.top_tags && stats.top_tags.length > 0 && (
          <div className="mb-6">
            <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
              <Filter className="h-3 w-3" />
              {t("legal_kb.filter_by_tag")}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="legal-kb-tags">
              {stats.top_tags.map(({ tag, count }) => {
                const active = activeTag === tag;
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setActiveTag(active ? null : tag)}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                      active
                        ? "bg-[#D4A937]/20 text-[#F4C955]"
                        : "bg-slate-800/60 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                    }`}
                    data-testid={`legal-kb-tag-${tag}`}
                  >
                    <span>{tag}</span>
                    <span className="text-[9px] text-slate-500">{count}</span>
                  </button>
                );
              })}
              {activeTag && (
                <button
                  type="button"
                  onClick={() => setActiveTag(null)}
                  className="rounded-full px-2 py-0.5 text-[10px] text-slate-500 hover:text-slate-300"
                  data-testid="legal-kb-tag-clear"
                >
                  {t("legal_kb.clear_tag")}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Notes list */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500" data-testid="legal-kb-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : notes.length === 0 ? (
          <Card className="border-dashed border-slate-700 bg-slate-900/30 p-10 text-center" data-testid="legal-kb-empty">
            <BookMarked className="mx-auto h-10 w-10 text-slate-600" />
            <p className="mt-4 font-medium text-slate-300">{t("legal_kb.empty_title")}</p>
            <p className="mt-2 text-sm text-slate-500">{t("legal_kb.empty_desc")}</p>
            <Button
              onClick={openCreate}
              className="mt-5 rounded-full bg-[#D4A937] font-bold text-[#05073B] hover:bg-[#E0B850]"
              data-testid="legal-kb-empty-new-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("legal_kb.new_note")}
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2" data-testid="legal-kb-notes">
            {notes.map((note) => {
              const Icon = SOURCE_ICON[note.source] || PencilLine;
              return (
                <Card
                  key={note.note_id}
                  className="group flex flex-col gap-3 border-slate-800 bg-slate-900/60 p-5 transition-colors hover:border-[#D4A937]/40"
                  data-testid={`legal-kb-note-${note.note_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-start gap-2">
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A937]" />
                      <h3 className="font-display text-base font-bold text-white line-clamp-2">
                        {note.title}
                      </h3>
                    </div>
                    {note.pinned && (
                      <Pin className="h-3.5 w-3.5 shrink-0 fill-[#D4A937] text-[#D4A937]" />
                    )}
                  </div>
                  {note.citation && (
                    <p className="text-[11px] font-mono text-[#F4C955]" data-testid={`legal-kb-citation-${note.note_id}`}>
                      {note.citation}
                    </p>
                  )}
                  <p className="text-sm leading-relaxed text-slate-300 line-clamp-4">
                    {note.body}
                  </p>
                  {note.tags && note.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {note.tags.slice(0, 6).map((tg) => (
                        <button
                          key={tg}
                          type="button"
                          onClick={() => setActiveTag(tg)}
                          className="rounded-full bg-slate-800/80 px-2 py-0.5 text-[10px] text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                        >
                          {tg}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="mt-auto flex items-center justify-between border-t border-slate-800 pt-3 text-[10px] uppercase tracking-wider text-slate-500">
                    <button
                      type="button"
                      onClick={() => handleUseInChat(note)}
                      className="inline-flex items-center gap-1 rounded-full bg-[#9ACD32]/15 px-2 py-0.5 text-[10px] font-bold text-[#9ACD32] transition-colors hover:bg-[#9ACD32]/25"
                      title={t("legal_kb.use_in_chat")}
                      data-testid={`legal-kb-use-in-chat-${note.note_id}`}
                    >
                      <Send className="h-3 w-3" />
                      <span>{t("legal_kb.use_in_chat")}</span>
                    </button>
                    <div className="flex items-center gap-1">
                      <span className="hidden sm:inline">{new Date(note.updated_at).toLocaleDateString(locale)}</span>
                      <button
                        type="button"
                        onClick={() => handleTogglePin(note)}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-[#D4A937]"
                        title={note.pinned ? t("legal_kb.unpin") : t("legal_kb.pin")}
                        data-testid={`legal-kb-pin-${note.note_id}`}
                      >
                        {note.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(note)}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
                        title={t("legal_kb.edit")}
                        data-testid={`legal-kb-edit-${note.note_id}`}
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(note)}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-red-400"
                        title={t("legal_kb.delete")}
                        data-testid={`legal-kb-delete-${note.note_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Disclaimer */}
        <p className="mt-10 rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-[11px] leading-relaxed text-slate-500">
          <Scale className="mr-1 inline h-3 w-3" />
          {t("legal_kb.disclaimer")}
        </p>
      </section>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-[#0B0E2E] text-slate-100" data-testid="legal-kb-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">
              {editing ? t("legal_kb.edit_title") : t("legal_kb.new_title")}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-3">
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t("legal_kb.field_title")}
                </label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder={t("legal_kb.field_title_ph")}
                  className="border-slate-700 bg-slate-900 text-white"
                  maxLength={300}
                  data-testid="legal-kb-form-title"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t("legal_kb.field_citation")}
                </label>
                <Input
                  value={form.citation}
                  onChange={(e) => setForm((f) => ({ ...f, citation: e.target.value }))}
                  placeholder={t("legal_kb.field_citation_ph")}
                  className="border-slate-700 bg-slate-900 font-mono text-sm text-white"
                  maxLength={400}
                  data-testid="legal-kb-form-citation"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t("legal_kb.field_body")}
                </label>
                <Textarea
                  value={form.body}
                  onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  placeholder={t("legal_kb.field_body_ph")}
                  rows={10}
                  className="border-slate-700 bg-slate-900 text-sm text-white"
                  maxLength={20000}
                  data-testid="legal-kb-form-body"
                />
                <p className="mt-1 text-[10px] text-slate-500">
                  {form.body.length} / 20.000
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {t("legal_kb.field_tags")}
                </label>
                <Input
                  value={form.tags}
                  onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                  placeholder={t("legal_kb.field_tags_ph")}
                  className="border-slate-700 bg-slate-900 text-sm text-white"
                  data-testid="legal-kb-form-tags"
                />
                <p className="mt-1 text-[10px] text-slate-500">{t("legal_kb.field_tags_hint")}</p>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              className="text-slate-300 hover:bg-slate-800"
              data-testid="legal-kb-form-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              className="bg-[#D4A937] font-bold text-[#05073B] hover:bg-[#E0B850]"
              data-testid="legal-kb-form-save"
            >
              {editing ? t("legal_kb.save_changes") : t("legal_kb.save_note")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
